// Plans an attack pattern (tuning.ts PATTERNS): when each opponent has to
// enter so the player can strike them in the authored order and rhythm.
// Pure; the Sim draws which pattern, kinds and sides, then queues the spawns.

import {
  FEINT,
  KICK,
  KINDS,
  PATTERN,
  PATTERNS,
  TUNING as T,
  type EnemyKind,
  type PatternDef,
  type PatternId,
  windupFor,
} from './tuning';
import type { RoundSpec, Side } from './sim';

/** Kinds that go down to one strike: what a pattern's open (null) steps draw from. */
export const ONE_HIT: readonly EnemyKind[] = (Object.keys(KINDS) as EnemyKind[]).filter(
  (k) => KINDS[k].hp === 1,
);

/**
 * Seconds from entering the mat to the moment the player should strike, for
 * an opponent walking at `v`: as it steps into reach; for a feinter after its
 * fake and rush; for a blocker into its windup; for a kicker as the kick
 * comes in. (Footwork pulses around the average speed, so this is close, not
 * exact; pattern members share their footwork, so their spacing holds.)
 */
export function strikeTravel(kind: EnemyKind, v: number, level: number): number {
  const walk = T.spawnDistance - T.reach;
  switch (kind) {
    case 'feinter':
      return (walk - FEINT.margin) / v + FEINT.duration + FEINT.margin / (v * FEINT.rushMult);
    case 'blocker':
      // Into the windup as far as at base pace: shorter windups, earlier strike
      return (
        (T.spawnDistance - T.attackRange) / v +
        (PATTERN.blockerReact * windupFor('blocker', level)) / KINDS.blocker.windup
      );
    case 'kicker':
      return (walk - KICK.stand) / v + windupFor('kicker', level) + KICK.stand / KICK.launchSpeed;
    default:
      return walk / v;
  }
}

/** Whether `kind` can come up in a round (its mix, or by level when it has none). */
export function inMix(spec: RoundSpec, level: number, kind: EnemyKind): boolean {
  return spec.kinds ? (spec.kinds[kind] ?? 0) > 0 : level >= KINDS[kind].fromLevel;
}

/** The patterns a round can send right now, with `left` opponents still to come. */
export function eligiblePatterns(spec: RoundSpec, level: number, left: number): PatternId[] {
  const oneHit = ONE_HIT.some((k) => inMix(spec, level, k));
  return (Object.keys(PATTERNS) as PatternId[]).filter((id) => {
    const p = PATTERNS[id];
    if (level < p.fromLevel || (p.toLevel !== undefined && level > p.toLevel)) return false;
    if (p.steps.length > left) return false;
    return p.steps.every(([k]) => (k === null ? oneHit : inMix(spec, level, k)));
  });
}

export interface PlannedSpawn {
  at: number;
  kind: EnemyKind;
  side: Side;
}

/**
 * When each member enters, sorted by time. `kinds` and `sides` are the steps'
 * resolved kinds and sides; `walk` is the round's walking speed, `beat` its
 * seconds per beat. The first strike falls no earlier than `earliest`, and no
 * one has to enter before `now`. `level` sets the windups (overtime).
 */
export function planPattern(
  def: PatternDef,
  kinds: readonly EnemyKind[],
  sides: readonly Side[],
  walk: number,
  beat: number,
  now: number,
  earliest: number,
  level: number,
): { spawns: PlannedSpawn[]; lastStrike: number } {
  const offsets: number[] = [];
  let t = 0;
  def.steps.forEach(([, , beats], i) => {
    if (i > 0) {
      let gap = beats === 0 ? PATTERN.pincerGap : Math.max(PATTERN.minGap, beats * beat);
      // From the same side: room to queue up behind the one before
      if (sides[i] === sides[i - 1])
        gap = Math.max(gap, T.queueSpacing / walk + PATTERN.queueMargin);
      t += gap;
    }
    offsets.push(t);
  });
  const travel = kinds.map((k) => strikeTravel(k, walk * KINDS[k].speedMult, level));
  const first = Math.max(earliest, ...travel.map((tr, i) => now + tr - offsets[i]!));
  const spawns = kinds
    .map((kind, i) => ({ at: first + offsets[i]! - travel[i]!, kind, side: sides[i]! }))
    .sort((a, b) => a.at - b.at);
  return { spawns, lastStrike: first + offsets.at(-1)! };
}
